import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Output,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {HttpErrorResponse} from '@angular/common/http';
import {SharedModule} from '../../../shared/module/shared.module';
import {NgSelectComponent} from '@ng-select/ng-select';
import {isPlatformBrowser} from '@angular/common';
import {finalize, Subject, takeUntil} from 'rxjs';
import {ToastService, SubscribersService, TokenStorageService, UtilityService, LoaderService} from '@coreServices';
import {AuthService, AccountService, SettingsService} from '@coreApiServices';
import {STORAGE, CommonConstants, RegexPatternConstants} from '@coreConstant';
import {
  RequestParamModel,
  TradeTypeModel,
  PaginationModel,
  AccountModel,
  GoogleAddressModel,
  FrontendUserVerificationCodeModel
} from '@coreModels';
import {NgxGpAutocompleteDirective, NgxGpAutocompleteModule} from '@angular-magic/ngx-gp-autocomplete';
import {Loader} from '@googlemaps/js-api-loader';
import {environment} from 'src/environments/environment';
import { HandleImageErrorDirective } from 'src/app/core/directives/handle-image-error.directive';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [ReactiveFormsModule, SharedModule, NgSelectComponent, FormsModule, NgxGpAutocompleteModule, HandleImageErrorDirective],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss', '../auth.component.scss'],
  providers: [
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: environment.GOOGLE_MAP_KEY,
        libraries: ['places']
      })
    },
  ],
  encapsulation: ViewEncapsulation.None
})
export class RegisterComponent {

  registerForm: any;
  isShowFormOtherField!: boolean;
  tradeLogo!: string | null;
  isShowTradeTypeDropdown!: boolean;
  userDetailsDisable!: boolean;
  tradeType!: TradeTypeModel[];
  RegexPatternConstants = RegexPatternConstants;
  selectedTradeType = 'Select';
  verificationCodeForm: any = FrontendUserVerificationCodeModel;
  submitted: boolean = false;
  businessRole: any;
  @ViewChild('confirmEmailInput') confirmEmailInput!: ElementRef<HTMLInputElement>;
  @Output() type: EventEmitter<string> = new EventEmitter();
  destroy$ = new Subject<void>();
  @ViewChild('ngxPlaces') placesRef!: NgxGpAutocompleteDirective;
  options = {
    componentRestrictions: {country: 'AU'},
    strictBounds: true,
    fields: ['ALL'],
    types: ['address'],
  };
  abnMaskConfiguration = {mask: '00 000 000 000', lazy: false};
  mobileMaskConfiguration = {mask: '00 000 000 00', lazy: false};
  addressModel: any = {};

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private toastService: ToastService,
    private authService: AuthService,
    private subscriberService: SubscribersService,
    public utilityService: UtilityService,
    private accountService: AccountService,
    private settingsService: SettingsService,
    @Inject(PLATFORM_ID) private platformId: any,
    private tokenStorageService: TokenStorageService,
    public loaderService: LoaderService
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.getTradeTypeList();
    this.getBusinessList();
  }

  createForm() {
    this.registerForm = this.fb.group({
      trade_type_id: [null, [Validators.required]],
      abn: ['', [Validators.required, this.abnValidator.bind(this)]],
      assigned_to_admin: [''],
      company_name: ['', [Validators.required, this.TrimmedRequiredValidator]],
      address_line_1: ['', [Validators.required, this.addressValidator.bind(this), this.TrimmedRequiredValidator]],
      address_line_2: '',
      job_title: '',
      suburb: ['', [Validators.required, this.TrimmedRequiredValidator]],
      town_city: ['', [Validators.required, this.TrimmedRequiredValidator]],
      state_region: ['', [Validators.required, this.TrimmedRequiredValidator]],
      post_code: ['', [Validators.required, this.TrimmedRequiredValidator]],
      country: ['', [Validators.required, this.TrimmedRequiredValidator]],
      website: ['', [Validators.required, Validators.pattern(RegexPatternConstants.URL_REGEX), this.TrimmedRequiredValidator]],
      first_name: ['', [Validators.required, this.noWhitespaceValidator, Validators.pattern(/^[a-zA-Z_'-]*$/), this.TrimmedRequiredValidator]],
      last_name: ['', [Validators.required, this.noWhitespaceValidator, Validators.pattern(/^[a-zA-Z_'-]*$/), this.TrimmedRequiredValidator]],
      business_role_id: [null, [Validators.required]],
      mobile: ['', [Validators.required, Validators.minLength(10), this.mobileValidator.bind(this)]],
      email: ['', [Validators.required, Validators.pattern(RegexPatternConstants.EMAIL_REGEX)]],
      password: ['', [Validators.required, Validators.minLength(6), this.TrimmedRequiredValidator]],
      confirm_password: ['', [Validators.required, this.passwordTradeMatcher.bind(this), this.TrimmedRequiredValidator]],
      confirm_email: ['', [Validators.required, Validators.pattern(RegexPatternConstants.EMAIL_REGEX), this.confirmMailTradeMatcher.bind(this)]],
      is_subscribed: [true] // Default value of checkbox
    });

    this.tf.email.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.tf.confirm_email.updateValueAndValidity());
    this.tf.password.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.tf.confirm_password.updateValueAndValidity());
  }

  getTradeTypeList() {
    let requestParamModel = new RequestParamModel();
    delete requestParamModel.pageSize;
    requestParamModel.fields = ['id', 'name'];
    requestParamModel.sortChange('name', 'asc');
    this.accountService.getTradeTypeLists(requestParamModel).pipe(takeUntil(this.destroy$)).subscribe((dataList: PaginationModel<any>) => {
      requestParamModel.update<any>(dataList);
      this.tradeType = dataList.data;
    }, (error: HttpErrorResponse) => {
      this.toastService.showError(error);
    });
  }

  getBusinessList() {
    let requestParamModel = new RequestParamModel();
    delete requestParamModel.pageSize;
    requestParamModel.fields = ['id', 'name'];
    requestParamModel.sortChange('name', 'asc');
    this.accountService.getBusinessRoleList(requestParamModel).pipe(takeUntil(this.destroy$)).subscribe((dataList: PaginationModel<any>) => {
      requestParamModel.update<any>(dataList);
      this.businessRole = dataList.data;
    }, (error: HttpErrorResponse) => {
      this.toastService.showError(error);
    });
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control && control.value && control.value.toString() || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : {'whitespace': true};
  }

  private mobileValidator(control: FormControl): { [s: string]: boolean } {
    if (this.registerForm && control.value !== '' && control.value !== null) {
      let $mobileNumber = control.value;
      if ($mobileNumber.substring(0, 2) == '04') {
        return {};
      }
    }
    return {mobileNotValid: true};
  }

  private addressValidator(control: FormControl): { [s: string]: boolean } {
    let addressRegex = CommonConstants.PO_BOX_ADDRESS_REGEX;

    if (this.registerForm && control.value !== '' && control.value !== null) {
      let $addressLine1 = control.value;
      if (!addressRegex.test($addressLine1)) {
        return {};
      }
    }
    return {addressNotValid: true};
  }

  private abnValidator(control: FormControl): { [s: string]: boolean } {
    if (this.registerForm && control.value !== '' && control.value !== null) {
      this.tradeLogo = '';
      this.isShowTradeTypeDropdown = false;
      this.registerForm.controls['trade_type_id'].setValue(null);
      this.registerForm.controls['trade_type_id'].enable();
      this.registerForm.controls['assigned_to_admin'].setValue('');
      this.registerForm.controls['assigned_to_admin'].enable();
      this.registerForm.controls['company_name'].setValue('');
      this.registerForm.controls['company_name'].enable();
      this.registerForm.controls['address_line_1'].setValue('');
      this.registerForm.controls['address_line_1'].enable();
      this.registerForm.controls['address_line_2'].setValue('');
      this.registerForm.controls['address_line_2'].enable();
      this.registerForm.controls['suburb'].setValue('');
      this.registerForm.controls['suburb'].enable();
      this.registerForm.controls['town_city'].setValue('');
      this.registerForm.controls['town_city'].enable();
      this.registerForm.controls['state_region'].setValue('');
      this.registerForm.controls['state_region'].enable();
      this.registerForm.controls['country'].setValue('');
      this.registerForm.controls['country'].enable();
      this.registerForm.controls['post_code'].setValue('');
      this.registerForm.controls['post_code'].enable();
      this.registerForm.controls['website'].setValue('');
      this.registerForm.controls['website'].enable();
      this.addressModel = {};
      this.userDetailsDisable = false;
      let $abn = control.value;
      const $weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

      //remove spaces from string
      $abn = $abn.replace(/\s+/g, '');
      // strip anything other than digits
      $abn = $abn.replace("/[^\d]/", "");

      // check length is 11 digits
      if ($abn.length == 11) {
        // apply ato check method
        let $sum = 0;
        $weights.forEach(function (value, i) {
          let $digit = $abn[i] - (i ? 0 : 1);
          $sum += value * $digit;
        });
        if (($sum % 89) == 0) {
          this.isShowFormOtherField = true;
          // this.globals.rightSidebarAccountLoader = false;
          // this.subscriberService.rightSidebarAccountLoaderToggle(this.globals.rightSidebarAccountLoader);
          let accountModel: any = {
            abn: $abn
          };
          this.accountService.getAccountByABN(accountModel).pipe(takeUntil(this.destroy$)).subscribe((account: AccountModel) => {
            if (account != undefined && account.id != undefined) {
              let s3AccountLogoURL = CommonConstants.S3_ACCOUNT_LOGO_IMAGE_KIT_URL
              let logoName = account.logo || '';
              if (logoName != '') {
                this.tradeLogo = s3AccountLogoURL + '/' + logoName;
              }
              if (account.company_name !== '' && account.company_name !== null) {
                this.userDetailsDisable = true;
                this.registerForm.controls['company_name'].disable();
                this.registerForm.controls['company_name'].setValue(account.company_name);
              }
              if (account && account.trade_type_id) {
                this.isShowTradeTypeDropdown = true;
                this.tradeType.forEach((ele: any) => {
                  if (ele.id === account.trade_type_id) {
                    this.changeTradeType(ele);
                  }
                });
              }
              if (account && account.frontendUsers && account.frontendUsers.length > 0) {
                this.registerForm.controls['assigned_to_admin'].setValue(account.frontendUsers[0].assigned_to_admin);
              }
              if (account.companyAddresses && account.companyAddresses.length > 0) {
                this.registerForm.controls['address_line_1'].setValue(account.companyAddresses[account.companyAddresses.length - 1].address_line_1);
                this.registerForm.controls['address_line_1'].enable();
                this.registerForm.controls['address_line_2'].setValue(account.companyAddresses[account.companyAddresses.length - 1].address_line_2);
                this.registerForm.controls['address_line_2'].enable();
                this.registerForm.controls['suburb'].setValue(account.companyAddresses[account.companyAddresses.length - 1].suburb);
                this.registerForm.controls['suburb'].enable();
                this.registerForm.controls['town_city'].setValue(account.companyAddresses[account.companyAddresses.length - 1].town_city);
                this.registerForm.controls['town_city'].enable();
                this.registerForm.controls['state_region'].setValue(account.companyAddresses[account.companyAddresses.length - 1].state_region);
                this.registerForm.controls['state_region'].enable();
                this.registerForm.controls['country'].setValue(account.companyAddresses[account.companyAddresses.length - 1].country);
                this.registerForm.controls['country'].enable();
                this.registerForm.controls['post_code'].setValue(+account.companyAddresses[account.companyAddresses.length - 1].post_code);
                this.registerForm.controls['post_code'].enable();
                this.addressModel['latitude'] = account.companyAddresses[account.companyAddresses.length - 1].latitude;
                this.addressModel['longitude'] = account.companyAddresses[account.companyAddresses.length - 1].longitude;
              }
              this.registerForm.controls['website'].setValue(account.website);
              this.registerForm.controls['website'].enable();
              // this.globals.rightSidebarAccountLoader = false;
              // this.subscriberService.rightSidebarAccountLoaderToggle(this.globals.rightSidebarAccountLoader);
            } else {
              // this.globals.rightSidebarAccountLoader = false;
              // this.subscriberService.rightSidebarAccountLoaderToggle(this.globals.rightSidebarAccountLoader);
            }
          }, (error: HttpErrorResponse) => {
            if (error.status == 422) {
              let self = this;
              if (error.error.data.length > 0) {
                Object.keys(error.error.data).forEach(function (key, data) {
                  self.registerForm.controls[error.error.data[key].field].setErrors({'apiError': error.error.data[key].message});
                });
              }
              // this.globals.rightSidebarAccountLoader = !false;
              // this.subscriberService.rightSidebarAccountLoaderToggle(this.globals.rightSidebarAccountLoader);
            }
          });
          return {};
        }
      }
      this.isShowFormOtherField = false;
      return {abnNotValid: true};
    }
    this.isShowFormOtherField = false;
    return {abnNotValid: true};
  }

  changeTradeType(trade: TradeTypeModel) {
    this.registerForm.controls['trade_type_id'].setValue(trade.id);
    this.selectedTradeType = trade.name;
  }

  private passwordTradeMatcher(control: AbstractControl): { [s: string]: boolean } {
    if (this.registerForm && (control.value !== this.registerForm.controls.password.value)) {
      return {passwordNotMatch: true};
    }
    return {};
  }

  private confirmMailTradeMatcher(control: FormControl): { [s: string]: boolean } {
    if (this.registerForm && (control.value?.toLowerCase() !== this.registerForm.controls.email.value?.toLowerCase())) {
      return {confirmEmailNotMatch: true};
    }
    return {};
  }

  get tf() {
    return this.registerForm.controls;
  }

  handleCompanyAddressChange(address: any) {
    this.tf['address_line_1'].setValue('');
    this.tf['address_line_2'].setValue('');
    this.tf['post_code'].setValue('');
    this.tf['suburb'].setValue('');
    this.tf['town_city'].setValue('');
    this.tf['state_region'].setValue('');
    this.tf['country'].setValue('');
    if (address !== undefined && address.formatted_address !== undefined) {
      let addressObject = this.utilityService.addressHandler(address) as GoogleAddressModel;
      if (addressObject) {
        this.tf['address_line_1'].setValue(addressObject.address_line_1);
        this.tf['address_line_2'].setValue(addressObject.address_line_2);
        this.tf['post_code'].setValue(addressObject.post_code);
        this.tf['suburb'].setValue(addressObject.suburb);
        this.tf['town_city'].setValue(addressObject.town_city);
        this.tf['state_region'].setValue(addressObject.state_region);
        this.tf['country'].setValue(addressObject.country);
        if (!addressObject.isStreetAddress) {
          this.tf['address_line_1'].setErrors({'streetRequired': CommonConstants.STREET_NUMBER_REQUIRED});
        }
      }
      if (address.geometry.location.toJSON()) {
        this.addressModel['latitude'] = address.geometry.location.toJSON().lat ? address.geometry.location.toJSON().lat : '';
        this.addressModel['longitude'] = address.geometry.location.toJSON().lng ? address.geometry.location.toJSON().lng : '';
      }
    } else {
      this.tf['address_line_1'].setValue(this.tf.address_line_1.value);
    }
    if (isPlatformBrowser(this.platformId)) {
      let addressLine = document.getElementById('address_line_2');
      addressLine?.focus();
    }
  }

  confirmEmailAddress(event: any) {
    if (event.type == 'keydown' || event.type == 'change') {
      this.confirmEmailInput.nativeElement.focus();
    }
  }

  logFormErrors(): void {
    Object.keys(this.registerForm.controls).forEach(key => {
      const control = this.registerForm.get(key);
      if (control && control.invalid) {
        this.toastService.showError(control.errors)
      }
    });
  }

  register() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      this.logFormErrors();
      this.registerForm.markAsTouched();
      this.registerForm.markAsDirty();
      return;
    }
    let data = {
      ...this.registerForm.getRawValue(),
      role: CommonConstants.USER_TYPE_TRADE,
      ...this.addressModel
    }
    this.verificationCodeForm = {
      email: this.tf.email.value
    }
    this.loaderService.show();
    this.authService.sendVerificationcode(this.verificationCodeForm).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.tokenStorageService.setItemStorage(STORAGE.EMAIL_KEY, this.verificationCodeForm.email);
        this.submitted = false;
        this.registerForm.reset();
        this.toastService.showSuccess(CommonConstants.SUCCESS_VERIFICATION_CODE);
        this.subscriberService.verificationFormToggle(data);
        this.type.emit('verification');
      },
      error: (error) => {
        if (error.status == 422) {
          let self = this;
          if (error.error.data.length > 0) {
            Object.keys(error.error.data).forEach(function (key, data) {
              self.registerForm.controls[error.error.data[key].field].setErrors({'apiError': error.error.data[key].message});
            });
          }
        }
        this.loaderService.hide();
      },
      complete: () => {
        this.loaderService.hide();
      }
    })
  }

  viewEligibilityCriteriaPdf() {
    this.loaderService.show();
    this.settingsService.getDownloadEligibilityCriteria().pipe(takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      ).subscribe((result: any) => {
          if (isPlatformBrowser(this.platformId)) {
            window.open(result, '_blank');
          }
        },
        (error: HttpErrorResponse) => {
          this.toastService.showError(error);
        }
      );
  }


  TrimmedRequiredValidator(control: AbstractControl) {
    if (control.value && typeof control.value != 'number') {
      if (control.value.trim() === '')
        return {required: true};
    }
    return null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
